import React, { useEffect, useState } from 'react';
import { LayoutDashboardIcon, UsersIcon, UserIcon, CalendarIcon, BriefcaseIcon, ShieldIcon, StethoscopeIcon, ContactIcon, TrendingUpIcon, SettingsIcon, LogOutIcon, FileTextIcon, ClipboardListIcon, MessageSquareIcon, ChevronsLeftIcon, ChevronsRightIcon, ChevronDownIcon, XIcon, BrainIcon, PercentIcon, PackageIcon, BotIcon, UserCircleIcon, LayersIcon, GiftIcon, AwardIcon, HomeIcon, ActivityIcon, Building2Icon, ServerIcon, TargetIcon } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { useData } from '../../contexts/DataContext';
import { ModulePermissions } from '../../types';
interface SidebarProps {
  currentModule: string;
  onModuleChange: (module: string) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  onMobileClose?: () => void;
}
const menuGroups = [{
  label: 'Principal',
  icon: HomeIcon,
  items: [{
    id: 'dashboard',
    label: 'Panel de Control',
    icon: LayoutDashboardIcon,
    permission: 'dashboard' as keyof ModulePermissions
  }, {
    id: 'patients',
    label: 'Pacientes',
    icon: UsersIcon,
    permission: 'patients' as keyof ModulePermissions
  }, {
    id: 'patient360',
    label: 'Cliente 360',
    icon: UserCircleIcon,
    permission: 'patient360' as keyof ModulePermissions
  }, {
    id: 'session-packages',
    label: 'Paquetes de Sesiones',
    icon: LayersIcon,
    permission: 'sessionPackages' as keyof ModulePermissions
  }, {
    id: 'appointments',
    label: 'Citas',
    icon: CalendarIcon,
    permission: 'appointments' as keyof ModulePermissions
  }, {
    id: 'clinical-history',
    label: 'Historial Clínico',
    icon: FileTextIcon,
    permission: 'clinicalHistory' as keyof ModulePermissions
  }, {
    id: 'goals',
    label: 'Metas y Alertas',
    icon: TargetIcon,
    permission: 'goals' as keyof ModulePermissions
  }]
}, {
  label: 'Clínica',
  icon: ActivityIcon,
  items: [{
    id: 'doctors',
    label: 'Doctores',
    icon: UserIcon,
    permission: 'doctors' as keyof ModulePermissions
  }, {
    id: 'services',
    label: 'Servicios',
    icon: BriefcaseIcon,
    permission: 'services' as keyof ModulePermissions
  }, {
    id: 'offers',
    label: 'Ofertas',
    icon: PercentIcon,
    permission: 'offers' as keyof ModulePermissions
  }, {
    id: 'gift-certificates',
    label: 'Certificados de Regalo',
    icon: GiftIcon,
    permission: 'giftCertificates' as keyof ModulePermissions
  }, {
    id: 'loyalty',
    label: 'Tarjetas de Fidelidad',
    icon: AwardIcon,
    permission: 'loyalty' as keyof ModulePermissions
  }, {
    id: 'products',
    label: 'Productos',
    icon: PackageIcon,
    permission: 'products' as keyof ModulePermissions
  }, {
    id: 'insurance',
    label: 'Seguros',
    icon: ShieldIcon,
    permission: 'insurance' as keyof ModulePermissions
  }, {
    id: 'specialties',
    label: 'Especialidades',
    icon: StethoscopeIcon,
    permission: 'specialties' as keyof ModulePermissions
  }]
}, {
  label: 'CRM',
  icon: Building2Icon,
  items: [{
    id: 'contacts',
    label: 'Contactos',
    icon: ContactIcon,
    permission: 'contacts' as keyof ModulePermissions
  }, {
    id: 'opportunities',
    label: 'Oportunidades',
    icon: TrendingUpIcon,
    permission: 'opportunities' as keyof ModulePermissions
  }, {
    id: 'follow-up-notes',
    label: 'Notas de Seguimiento',
    icon: MessageSquareIcon,
    permission: 'followUpNotes' as keyof ModulePermissions
  }, {
    id: 'commissions',
    label: 'Comisiones',
    icon: PercentIcon,
    permission: 'commissions' as keyof ModulePermissions
  }, {
    id: 'intelligence',
    label: 'Inteligencia',
    icon: BrainIcon,
    permission: 'intelligence' as keyof ModulePermissions
  }, {
    id: 'bot-omnichannel',
    label: 'Bot Omnichannel',
    icon: BotIcon,
    permission: 'botOmnichannel' as keyof ModulePermissions
  }]
}, {
  label: 'Sistema',
  icon: ServerIcon,
  items: [{
    id: 'audit-log',
    label: 'Bitácora',
    icon: ClipboardListIcon,
    permission: 'auditLog' as keyof ModulePermissions
  }, {
    id: 'settings',
    label: 'Configuración',
    icon: SettingsIcon,
    permission: 'settings' as keyof ModulePermissions
  }]
}];
const LOGO_URL = '/images.jpg';
export function Sidebar({
  currentModule,
  onModuleChange,
  isCollapsed,
  onToggleCollapse,
  onMobileClose
}: SidebarProps) {
  const {
    logout,
    user
  } = useAuth();
  const {
    canView
  } = usePermissions();
  const {
    isLoading
  } = useData();
  // Grupo que contiene el módulo actualmente activo
  const activeGroupLabel = menuGroups.find(g => g.items.some(it => it.id === currentModule))?.label ?? 'Principal';
  // Acordeón: qué secciones están abiertas. Por defecto solo la del módulo activo.
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => ({
    [activeGroupLabel]: true
  }));
  // Al navegar a un módulo de otra sección, abre esa sección automáticamente.
  useEffect(() => {
    setOpenGroups(prev => ({
      ...prev,
      [activeGroupLabel]: true
    }));
  }, [activeGroupLabel]);
  const toggleGroup = (label: string) => {
    setOpenGroups(prev => ({
      ...prev,
      [label]: !prev[label]
    }));
  };
  return <aside className={`h-screen bg-white dark:bg-[#0D1117] border-r border-gray-100 dark:border-white/[0.05] flex flex-col overscroll-contain transition-all duration-300 ease-in-out shadow-[1px_0_0_0_rgba(0,0,0,0.06)] dark:shadow-none md:shadow-none ${isCollapsed ? 'w-[68px] overflow-y-auto overflow-x-visible' : 'w-72 md:w-64 overflow-y-auto'}`} style={{
    scrollbarWidth: 'thin',
    scrollbarColor: 'rgba(156, 163, 175, 0.2) transparent'
  }} data-id="element-244" data-mp="2zpn4nkxlk4l4">
      
      {/* Logo + collapse + close */}
      <div className="flex-shrink-0 px-3 border-b border-gray-100 dark:border-white/[0.05] flex items-center justify-between min-h-[65px] gap-2" data-id="element-245" data-mp="jzamekdkh487">
        <button onClick={() => onModuleChange('dashboard')} className={`flex items-center gap-3 overflow-hidden hover:opacity-80 transition-opacity min-w-0 flex-1 ${isCollapsed ? 'justify-center' : ''}`} title="Ir al Panel de Control" data-id="element-246" data-mp="2devj64zcqvhz">
          
          {/* Logo con fallback de iniciales */}
          <div className="relative flex-shrink-0 w-9 h-9" data-id="element-247" data-mp="2w6obryaieep6">
            {/* Fallback always behind */}
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#1BACA6] to-[#1890C8] flex items-center justify-center" data-id="element-248" data-mp="316j0bv6tas72">
              <span className="text-white font-bold text-sm select-none" data-id="element-249" data-mp="2n5ztushzvu70">
                S
              </span>
            </div>
            {/* Image on top, clips itself */}
            <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/krfvfepnmlprsyech1wcsf/b2980652-5950-4085-85b3-5afd8262465c/images.jpg" alt="Savidna Logo" className="absolute inset-0 w-9 h-9 rounded-full object-cover" onError={e => {
            e.currentTarget.style.display = 'none';
          }} data-id="element-250" data-mp="1bqb7a28nkx6q" />
            
          </div>

          {!isCollapsed && <div className="min-w-0 text-left" data-id="element-251" data-mp="1i58meyj7hm4o">
              <h1 className="text-sm font-bold text-gray-900 dark:text-white truncate leading-tight" data-id="element-252" data-mp="1d9jmgu39y37u">
                Savidna
              </h1>
              <p className="text-[11px] text-gray-400 dark:text-gray-500 truncate" data-id="element-253" data-mp="pgkx8rvc6clu">
                CRM Médico
              </p>
            </div>}
        </button>

        {/* Mobile close */}
        {onMobileClose && <button onClick={onMobileClose} className="md:hidden flex-shrink-0 p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/[0.06] rounded-lg transition-colors" aria-label="Cerrar menú" data-id="element-254" data-mp="uia7446tok8c">
          
            <XIcon className="w-5 h-5" data-id="element-255" data-mp="3cffm65z9uez3" />
          </button>}
      </div>

      {/* Collapse toggle — desktop, below header */}
      <button onClick={onToggleCollapse} className="flex-shrink-0 hidden md:flex ml-3 my-2 items-center justify-center w-7 h-7 rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-white/[0.05] dark:hover:bg-white/[0.09] text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-all duration-200" title={isCollapsed ? 'Expandir menú' : 'Colapsar menú'} data-id="element-256" data-mp="3hdpjon8gxrnv">
        
        {isCollapsed ? <ChevronsRightIcon className="w-3.5 h-3.5" data-id="element-257" data-mp="465zgl9pgqod" /> : <ChevronsLeftIcon className="w-3.5 h-3.5" data-id="element-258" data-mp="8yfjxls1wyj9" />}
      </button>

      {/* User info pill — mobile only */}
      {!isCollapsed && user && <div className="flex-shrink-0 md:hidden mx-3 mt-3 px-3 py-2.5 bg-gradient-to-r from-[#1BACA6]/8 to-[#1890C8]/5 dark:from-cyan-500/10 dark:to-teal-500/5 rounded-xl border border-[#1BACA6]/15 dark:border-cyan-500/10 flex items-center gap-2.5" data-id="element-259" data-mp="dzv1v4yxmoxf">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#1BACA6] to-[#1890C8] flex items-center justify-center flex-shrink-0 shadow-sm shadow-[#1BACA6]/25" data-id="element-260" data-mp="27gralcobgu9e">
            {user.avatar ? <img src={user.avatar} alt={user.name} className="w-8 h-8 rounded-full object-cover" data-id="element-261" data-mp="1yf1dbq7r713p" /> : <span className="text-white font-bold text-sm" data-id="element-262" data-mp="1tiklakxfo2lc">
                {user.name?.charAt(0).toUpperCase()}
              </span>}
          </div>
          <div className="min-w-0" data-id="element-263" data-mp="2m55e7ptk4mr0">
            <p className="text-sm font-semibold text-gray-900 dark:text-white truncate" data-id="element-264" data-mp="2m6706pc42tni">
              {user.name}
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500 truncate" data-id="element-265" data-mp="2cfltsfq74ku1">
              {user.email}
            </p>
          </div>
        </div>}

      {/* Navigation */}
      <nav className="flex-shrink-0 py-1.5 px-2 space-y-0.5" data-id="element-266" data-mp="39x2m55tvyumu">
        {menuGroups.map(group => {
        const visibleItems = group.items.filter(item => user && canView(item.permission));
        if (visibleItems.length === 0) return null;
        const GroupIcon = group.icon;
        const isOpen = !!openGroups[group.label];
        const groupHasActive = visibleItems.some(it => it.id === currentModule);
        // COLAPSADO: un ícono por GRUPO. Al pasar el mouse se despliega un
        // flyout lateral con los módulos de esa sección (menú agrupado).
        if (isCollapsed) {
          return <div key={group.label} className="relative group/flyout" data-id="element-267" data-mp="g7v9g6v2q8lb">
                <button onClick={() => {
              if (visibleItems[0]) onModuleChange(visibleItems[0].id);
            }} title={group.label} className={`
                    w-full flex items-center justify-center py-2.5 rounded-xl relative transition-all duration-200
                    ${groupHasActive ? 'bg-gradient-to-r from-[#1BACA6]/10 to-[#1890C8]/8 dark:from-cyan-500/15 dark:to-teal-500/8 text-[#1BACA6] dark:text-cyan-400' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-white/[0.04] hover:text-gray-800 dark:hover:text-gray-200'}
                  `} data-id="element-268" data-mp="uvzw1lx8z49d">
                  
                  {groupHasActive && <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-6 bg-gradient-to-b from-[#1BACA6] to-[#1890C8] rounded-r-full" data-id="element-269" data-mp="1eh9r39vt3cxv" />}
                  <GroupIcon className="w-[19px] h-[19px] flex-shrink-0" data-id="element-270" data-mp="14kwy6e835346" />
                </button>

                {/* Flyout con los módulos del grupo */}
                <div className="invisible opacity-0 group-hover/flyout:visible group-hover/flyout:opacity-100 transition-all duration-150 absolute left-full top-0 ml-2 z-50 pointer-events-none group-hover/flyout:pointer-events-auto" data-id="element-271" data-mp="qrffevn83uf7">
                  <div className="min-w-[190px] bg-white dark:bg-[#161B22] border border-gray-100 dark:border-white/[0.08] rounded-xl shadow-xl shadow-black/[0.08] dark:shadow-black/40 p-1.5" data-id="element-272" data-mp="lup4emq9zp0n">
                    <p className="px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-gray-400 dark:text-gray-500 select-none" data-id="element-273" data-mp="2is354rmfe15h">
                      {group.label}
                    </p>
                    <ul className="space-y-0.5" data-id="element-274" data-mp="29sgaqps45vlx">
                      {visibleItems.map(item => {
                    const Icon = item.icon;
                    const isActive = currentModule === item.id;
                    return <li key={item.id} data-id="element-275" data-mp="32ehjs8f11hax">
                            <button onClick={() => onModuleChange(item.id)} className={`
                                w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[13px] font-medium transition-all duration-150
                                ${isActive ? 'bg-gradient-to-r from-[#1BACA6]/10 to-[#1890C8]/8 dark:from-cyan-500/15 dark:to-teal-500/8 text-[#1BACA6] dark:text-cyan-400' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/[0.05]'}
                              `} data-id="element-276" data-mp="2xgpuzn9oibay">
                              
                              <Icon className="w-[16px] h-[16px] flex-shrink-0" data-id="element-277" data-mp="1gwgw61rn7mlw" />
                              <span className="truncate" data-id="element-278" data-mp="1loqjrb5acal0">{item.label}</span>
                            </button>
                          </li>;
                  })}
                    </ul>
                  </div>
                </div>
              </div>;
        }
        // EXPANDIDO: encabezado clicable (acordeón) + ítems desplegables.
        return <div key={group.label} data-id="element-279" data-mp="1zlx254m3k79p">
              <button onClick={() => toggleGroup(group.label)} aria-expanded={isOpen} className={`
                  w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl transition-all duration-200 group
                  ${groupHasActive && !isOpen ? 'text-[#1BACA6] dark:text-cyan-400 bg-[#1BACA6]/[0.06] dark:bg-cyan-500/10' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/[0.04]'}
                `} data-id="element-280" data-mp="24nmaudluk8um">
                
                <span className={`flex-shrink-0 flex items-center justify-center w-7 h-7 rounded-lg transition-colors duration-200 ${groupHasActive ? 'bg-gradient-to-br from-[#1BACA6] to-[#1890C8] text-white shadow-sm shadow-[#1BACA6]/25' : 'bg-gray-100 dark:bg-white/[0.05] text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-200'}`} data-id="element-281" data-mp="29rdcwpzntcq6">
                  
                  <GroupIcon className="w-4 h-4" data-id="element-282" data-mp="1qs94njfxzmzk" />
                </span>
                <span className="flex-1 text-left text-[13px] font-semibold truncate" data-id="element-283" data-mp="1m1j2ef2tc093">
                  {group.label}
                </span>
                {groupHasActive && !isOpen && <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-[#1BACA6] dark:bg-cyan-400" data-id="element-284" data-mp="y9c2j5xvpb4x" />}
                <ChevronDownIcon className={`flex-shrink-0 w-4 h-4 text-gray-400 dark:text-gray-500 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} data-id="element-285" data-mp="34zfcdz23wp1x" />
                
              </button>

              {/* Ítems desplegables con transición de altura */}
              <div className={`grid transition-all duration-300 ease-in-out ${isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`} data-id="element-286" data-mp="30y3s89204w14">
                
                <div className="overflow-hidden" data-id="element-287" data-mp="3spjdnzrtocpr">
                  <ul className="mt-0.5 mb-1 ml-[26px] pl-3 border-l border-gray-100 dark:border-white/[0.06] space-y-0.5" data-id="element-288" data-mp="3so8cm7prhj2c">
                    {visibleItems.map(item => {
                  const Icon = item.icon;
                  const isActive = currentModule === item.id;
                  return <li key={item.id} data-id="element-289" data-mp="239a7wc8nlhvg">
                          <button onClick={() => onModuleChange(item.id)} className={`
                              w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg
                              text-[13px] font-medium transition-all duration-200 relative
                              ${isActive ? 'bg-gradient-to-r from-[#1BACA6]/10 to-[#1890C8]/8 dark:from-cyan-500/15 dark:to-teal-500/8 text-[#1BACA6] dark:text-cyan-400' : 'text-gray-500 dark:text-gray-500 hover:bg-gray-50 dark:hover:bg-white/[0.04] hover:text-gray-800 dark:hover:text-gray-200'}
                            `} data-id="element-290" data-mp="28a7owlhxg5vd">
                            
                            <Icon className={`w-[16px] h-[16px] flex-shrink-0 transition-colors duration-200 ${isActive ? 'text-[#1BACA6] dark:text-cyan-400' : ''}`} data-id="element-291" data-mp="wge39ozw1vfl" />
                            
                            <span className="truncate" data-id="element-292" data-mp="2quorpwckmnku">{item.label}</span>
                          </button>
                        </li>;
                })}
                  </ul>
                </div>
              </div>
            </div>;
      })}
      </nav>

      <div className="flex-grow" data-id="element-293" data-mp="2td0k3gbszy9z" />

      {/* Logout */}
      <div className="flex-shrink-0 p-3 border-t border-gray-100 dark:border-white/[0.05]" style={{
      marginBottom: 'env(safe-area-inset-bottom)'
    }} data-id="element-294" data-mp="2ycmy3rzghiju">
        
        <button onClick={() => {
        // Remove all session keys directly to guarantee clean logout
        try {
          localStorage.removeItem('savidna-session');
          localStorage.removeItem('savidna-user-id');
          const sbKey = Object.keys(localStorage).find(k => k.startsWith('sb-') && k.endsWith('-auth-token'));
          if (sbKey) localStorage.removeItem(sbKey);
          sessionStorage.clear();
        } catch {}
        window.location.href = window.location.origin;
      }} title={isCollapsed ? 'Cerrar Sesión' : undefined} className={`w-full flex items-center gap-3 rounded-xl text-[13px] font-medium text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 transition-all duration-200 ${isCollapsed ? 'px-0 py-2.5 justify-center' : 'px-3 py-2.5'}`} data-id="element-295" data-mp="382pn7h3giccw">
          
          <LogOutIcon className="w-[18px] h-[18px] flex-shrink-0" data-id="element-296" data-mp="zhxu1jbce922" />
          {!isCollapsed && <span data-id="element-297" data-mp="upmi1i3buwt5">Cerrar Sesión</span>}
        </button>
      </div>
      {/* Mobile spacer to clear BottomNav */}
      <div className="flex-shrink-0 md:hidden h-14" aria-hidden="true" data-id="element-298" data-mp="18grk0qu6416n" />
    </aside>;
}